import { useEffect, useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { cloneDeep } from "lodash";
import { useNavigate, useParams } from "react-router";
import { FilterItem, SDLCMetadata } from "@sscs/types";
import { Toast } from "@sscs/ui";
import { compareValues, getPageItems, sortItems } from "@sscs/utils";
import {
  INSIGHTS_ROUTE,
  INSIGHTS_SEARCH_ROUTE,
  REPORT_RETURN_TO_PREFIX,
  STORE_INSIGHTS_SEARCH_ID,
} from "Constants";
import { useAppDispatch, useAppSelector, useGroup } from "Hooks";
import { addUnsavedChange, removeUnsavedChange } from "Modules/notification";
import { ReportNavigationState } from "Types";
import { apiErrorToast } from "Utils";
import {
  useCreateInsightsSearchMutation,
  useDeleteInsightsSearchMutation,
  useGetInsightsSearchQuery,
  useGetMySearchesQuery,
  useSearchInsightsQuery,
  useUpdateInsightsSearchMutation,
} from "../services/insights.api";
import {
  clearGroupFilters,
  filterUpdated,
  selectInsightsFilters,
  selectIsInsightsSearchDirty,
  selectPagination,
  selectSort,
} from "../store";
import {
  Insight,
  InsightsFilters,
  InsightsResults,
  InsightsSearchSortKeys,
  MyInsightsSearch,
} from "../types";
import {
  RISK_STATUS_TO_WEIGHT,
  getMostSignificantEvaluation,
  getTotalReleaseStatusCount,
} from "../utils";

type InsightSortCases = {
  [key in InsightsSearchSortKeys]?: (
    first: Insight,
    second: Insight,
    reverse: boolean,
  ) => number;
};

function secondarySort<T>(
  firstCount: T,
  secondCount: T,
  firstStatus: SDLCMetadata.assessment_status_t | undefined,
  secondStatus: SDLCMetadata.assessment_status_t | undefined,
  reverse: boolean,
): number {
  return firstCount !== secondCount
    ? compareValues(firstCount, secondCount, reverse)
    : compareValues(
        RISK_STATUS_TO_WEIGHT[firstStatus ?? "unknown"],
        RISK_STATUS_TO_WEIGHT[secondStatus ?? "unknown"],
        reverse,
      );
}

export const insightSortCases: InsightSortCases = {
  affected_packages: (first, second, reverse) =>
    secondarySort(
      getTotalReleaseStatusCount(first.counters.affected_packages),
      getTotalReleaseStatusCount(second.counters.affected_packages),
      getMostSignificantEvaluation(first.risk_assessment_evaluation)?.status,
      getMostSignificantEvaluation(second.risk_assessment_evaluation)?.status,
      reverse,
    ),
  affected_versions: (first, second, reverse) =>
    secondarySort(
      getTotalReleaseStatusCount(first.counters.affected_versions),
      getTotalReleaseStatusCount(second.counters.affected_versions),
      getMostSignificantEvaluation(first.risk_assessment_evaluation)?.status,
      getMostSignificantEvaluation(second.risk_assessment_evaluation)?.status,
      reverse,
    ),
  effort: (first, second, reverse) =>
    secondarySort(
      first.effort,
      second.effort,
      getMostSignificantEvaluation(first.risk_assessment_evaluation)?.status,
      getMostSignificantEvaluation(second.risk_assessment_evaluation)?.status,
      reverse,
    ),
  introduced: (first, second, reverse) =>
    secondarySort(
      first.first_seen,
      second.first_seen,
      getMostSignificantEvaluation(first.risk_assessment_evaluation)?.status,
      getMostSignificantEvaluation(second.risk_assessment_evaluation)?.status,
      reverse,
    ),
};

const useSearch = () => {
  const dispatch = useAppDispatch();
  const { groupId, searchId } = useParams();
  const navigate = useNavigate();
  const currentGroup = useGroup();

  const [createSearch] = useCreateInsightsSearchMutation();
  const [updateSearch] = useUpdateInsightsSearchMutation();
  const [deleteSearch] = useDeleteInsightsSearchMutation();

  const { data: mySearches } = useGetMySearchesQuery(
    groupId ? { groupId } : skipToken,
  );
  const savedSearchQuery = useGetInsightsSearchQuery(
    searchId ? { id: searchId } : skipToken,
  );
  const savedSearch = {
    ...savedSearchQuery,
    data:
      savedSearchQuery.currentData ??
      (savedSearchQuery.isFetching ? savedSearchQuery.data : undefined),
  };

  const filters = useAppSelector((state) =>
    selectInsightsFilters(state, groupId, savedSearch.data),
  );
  const pagination = useAppSelector((state) =>
    selectPagination(state, groupId),
  );
  const sort = useAppSelector((state) => selectSort(state, groupId));

  const searchQueryResult = useSearchInsightsQuery(
    groupId && Object.keys(filters).length > 0
      ? {
          filters,
          groupId,
        }
      : skipToken,
  );

  const isQuickSearch = searchId !== undefined && searchId.startsWith("QS");
  const isSharedSearch =
    searchId !== undefined &&
    mySearches !== undefined &&
    savedSearch.currentData &&
    !isQuickSearch &&
    !mySearches.some((filter) => filter.id === searchId);
  const isLocalFilterDirty = useAppSelector((state) =>
    selectIsInsightsSearchDirty(state, groupId, savedSearch.data),
  );
  const isDirty = isLocalFilterDirty || isSharedSearch;
  const sortedResults = useMemo(
    () =>
      sortItems<Insight, keyof Insight, InsightsSearchSortKeys>(
        searchQueryResult.data?.issues ?? [],
        sort,
        insightSortCases,
      ),
    [searchQueryResult.data?.issues, sort],
  );

  const canUpdateSearch =
    searchId && mySearches
      ? mySearches.some((filter) => filter.id === searchId)
      : false;

  useEffect(() => {
    if (!groupId) return;

    if (isDirty) {
      const existingSearch =
        searchId !== undefined && !searchId.startsWith("QS");
      const text = existingSearch
        ? `Unsaved insights search ${savedSearch.data?.name} in group ${currentGroup?.name}`
        : `Unsaved new insights search in group ${currentGroup?.name}`;
      const navigateTo = existingSearch
        ? `/${INSIGHTS_ROUTE}/group/${groupId}/${INSIGHTS_SEARCH_ROUTE}/${searchId}`
        : `/${INSIGHTS_ROUTE}/group/${groupId}/${INSIGHTS_SEARCH_ROUTE}`;

      dispatch(
        addUnsavedChange({
          page: "insights",
          id: groupId,
          unsavedChange: {
            text: text,
            navigateTo: navigateTo,
            matchingRoute: `/${INSIGHTS_ROUTE}/group/${groupId}/${INSIGHTS_SEARCH_ROUTE}`,
            remindMe: true,
          },
        }),
      );
    }
  }, [dispatch, currentGroup, isDirty, groupId, savedSearch.data, searchId]);

  const handleSaveFilter = async (searchName: string) => {
    try {
      const createdSearch = await createSearch({
        body: {
          name: searchName,
          filters: filters,
        },
      }).unwrap();

      Toast.show({
        type: "success",
        title: `Search ${searchName} created successfully!`,
      });

      navigate(`../${INSIGHTS_SEARCH_ROUTE}/${createdSearch.id}`, {
        replace: true,
      });
      dispatch(clearGroupFilters(groupId));
    } catch (error) {
      apiErrorToast(error, "Could not create search");
    }
  };

  const handleUpdateFilter = async () => {
    if (!searchId || !savedSearch.data) {
      return;
    }

    try {
      await updateSearch({
        id: searchId,
        body: {
          filters: filters,
        },
      }).unwrap();

      Toast.show({
        type: "success",
        title: `Search ${savedSearch.data.name} updated successfully!`,
      });
      dispatch(clearGroupFilters(groupId));
    } catch (error) {
      apiErrorToast(error, "Could not update search");
    }
  };

  const handleFilterChange = (newFilters: InsightsFilters) => {
    if (!groupId) return;

    if (Object.keys(newFilters).length === 0) {
      dispatch(removeUnsavedChange({ page: "insights", id: groupId }));
    }

    if (isQuickSearch || (searchId && Object.keys(newFilters).length === 0)) {
      navigate(`../${INSIGHTS_SEARCH_ROUTE}`);
    }

    dispatch(
      filterUpdated({
        groupId: groupId,
        savedSearchFilters: savedSearch.data?.filters ?? {},
        filters: newFilters,
      }),
    );
  };

  const handleChipClick = (
    filterName: keyof InsightsFilters,
    filterItem: FilterItem,
  ) => {
    const newFilters = cloneDeep(filters);
    const selectedFilter = newFilters[filterName];

    if (selectedFilter) {
      selectedFilter.values = Array.from(
        new Set(selectedFilter.values.concat(filterItem.values)),
      );

      if (selectedFilter.operation === "is") {
        selectedFilter.operation = "is_any";
      } else if (selectedFilter.operation === "is_not") {
        selectedFilter.operation = "is_not_any";
      }
    } else {
      newFilters[filterName] = filterItem;
    }

    handleFilterChange(newFilters);
  };

  const handleDeleteSearch = async (search: MyInsightsSearch) => {
    try {
      // If a user is deleting the currently opened search, navigate to the new search
      if (searchId === search.id) {
        navigate(INSIGHTS_SEARCH_ROUTE, { replace: true });
        handleFilterChange(search.filters);
      }

      // If a user is deleting the stored search, remove it from local storage
      if (localStorage.getItem(STORE_INSIGHTS_SEARCH_ID) === search.id) {
        localStorage.removeItem(STORE_INSIGHTS_SEARCH_ID);
      }

      // Edit close report return pathname so it doesn't end up on non-existing search
      for (let index = 0; index < sessionStorage.length; index++) {
        const storageKey = sessionStorage.key(index);

        if (!storageKey || !storageKey.startsWith(REPORT_RETURN_TO_PREFIX)) {
          continue;
        }

        const returnToRawData = sessionStorage.getItem(storageKey);

        if (returnToRawData && returnToRawData.includes(search.id)) {
          const returnTo = JSON.parse(returnToRawData) as ReportNavigationState;
          const insightsRoute = returnTo.from.slice(
            0,
            returnTo.from.indexOf(INSIGHTS_SEARCH_ROUTE),
          );

          sessionStorage.setItem(
            storageKey,
            JSON.stringify({
              from: insightsRoute,
            } as ReportNavigationState),
          );
        }
      }

      await deleteSearch({ id: search.id }).unwrap();

      Toast.show({
        type: "success",
        title: `Search ${search.name} deleted successfully!`,
      });
    } catch (_error) {
      apiErrorToast(500, `Could not delete search ${search.name}`);
    }
  };

  return {
    canUpdateSearch,
    filters,
    isDirty,
    isSearchFetching:
      savedSearch.isFetching ||
      savedSearch.isLoading ||
      searchQueryResult.isFetching ||
      searchQueryResult.isLoading,
    pagination,
    savedSearch,
    searchQueryResult: {
      ...searchQueryResult,
      data: {
        estimated_count: searchQueryResult.data?.estimated_count ?? 0,
        match_count: searchQueryResult.data?.match_count ?? 0,
        issues: getPageItems(
          sortedResults,
          pagination.currentPage,
          pagination.itemsPerPage,
        ),
      } satisfies InsightsResults,
    },
    sort,
    handleChipClick,
    handleDeleteSearch,
    handleFilterChange,
    handleSaveFilter,
    handleUpdateFilter,
  };
};

export default useSearch;
